import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'bem-cn';

import { RoutePaths } from '../../constants';
import { apiClient } from '../../utils/api';
import { getQueryParam } from '../../utils/queryParams/queryParams';
import { useNavigateBack } from '../../hooks';
import { useAppDispatch } from '../../store/hooks';
import { promptsActions } from '../../store/slices/prompts';
import { api } from '../../store/api';
import { ApiTags } from '../../store/api/apiTags';

import { WebAppLayout } from '../../layouts/WebAppLayout';
import { NavigationElements } from '../../components/Navigation';
import { HeaderElements } from '../../components/Header/Header.types';
import { Icon, Stack, Steps } from '../../components/UI';
import { Step, StepStatus } from '../../components/UI/Steps/Steps.types';

import { NavigationElement } from '../../components/Navigation/Navigation.types';
import { PromptTextStep } from './PromptTextStep';
import { PromptDetailsStep } from './PromptDetailsStep';
import { PromptPreviewStep } from './PromptPreviewStep';
import { ValidationErrors, validateStep0, validateStep1 } from './NewPromptPage.helpers';
import { useLocalStorageForm } from './NewPromptPage.hooks';

import './NewPromptPage.scss';

const b = cn('new-prompt-page');

export const NewPromptPage = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { formData, updateFormData, clearFormData, isFormEmpty } = useLocalStorageForm();
    const { step, promptText, promptTitle, promptProblem, selectedCategories } = formData;

    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

    const navigateBack = useNavigateBack();

    // Проверка query параметра set_prompt при загрузке страницы
    useEffect(() => {
        const setPromptParam = getQueryParam('set_prompt');
        if (setPromptParam) {
            // Выполняем reset формы
            clearFormData();
            setValidationErrors({});

            // Заполняем promptText значением из параметра
            updateFormData({ promptText: setPromptParam });
        }
    }, [clearFormData, updateFormData]);

    const sendToModeration = async () => {
        try {
            const promptData = {
                title: promptTitle,
                description: promptProblem,
                prompt: promptText,
                categories: selectedCategories,
            };

            const response = await apiClient.post('/prompts', promptData);

            // @ts-expect-error пока без типа
            if (response.error) {
                navigate(RoutePaths.NewPromptFailure);
            } else {
                // Инвалидируем кэш "Мои промпты" в Redux slice
                dispatch(promptsActions.clearMyFeedCache());

                // Инвалидируем RTK Query кэш для "Мои промпты"
                dispatch(api.util.invalidateTags([{ type: ApiTags.Prompts, id: 'MY' }]));

                clearFormData();
                navigate(RoutePaths.NewPromptSuccess);
            }
        } catch (error) {
            console.error('Ошибка при отправке промпта:', error);
            navigate(RoutePaths.NewPromptFailure);
        }
    };

    const handleNextStep = () => {
        let errors: ValidationErrors = {};

        if (step === 0) {
            errors = validateStep0(promptText);
            setValidationErrors(errors);

            if (Object.keys(errors).length === 0) {
                updateFormData({ step: step + 1 });
            }
        } else if (step === 1) {
            errors = validateStep1(promptText, promptTitle, promptProblem, selectedCategories);
            setValidationErrors(errors);

            if (Object.keys(errors).length === 0) {
                updateFormData({ step: step + 1 });
            }
        } else if (step === 2) {
            sendToModeration();
        }
    };

    const handlePrevStep = () => {
        if (step === 0) {
            navigateBack();

            return;
        }

        updateFormData({ step: step - 1 });
    };

    const handleChangeCategories = (newSelectedCategories: string[]) => {
        updateFormData({ selectedCategories: newSelectedCategories });
        delete validationErrors.categories;
    };

    const handlePromptTextChange = (value: string) => {
        updateFormData({ promptText: value });
        delete validationErrors.prompt;
    };

    const handlePromptTitleChange = (value: string) => {
        updateFormData({ promptTitle: value });
        delete validationErrors.title;
    };

    const handlePromptProblemChange = (value: string) => {
        updateFormData({ promptProblem: value });
        delete validationErrors.problem;
    };

    const reset = () => {
        clearFormData();
        setValidationErrors({});
    };

    const getSteps = (): Step[] => {
        return [
            {
                text: 'Заявка',
                status: StepStatus.Pending,
                icon: 'application',
            },
            {
                text: 'Модерация',
                status: StepStatus.Inactive,
                icon: 'time',
            },
            {
                text: 'Публикация',
                status: StepStatus.Inactive,
                icon: 'tick',
            },
        ];
    };

    const getNavigationElements = (position: 'desktop' | 'mobile'): NavigationElement[] => {
        switch (step) {
            case 0:
            case 1:
                return [
                    {
                        text: position === 'desktop' ? 'Очистить форму' : 'Очистить',
                        icon: 'trash',
                        className: position === 'mobile' ? b('mobile-nav-button') : undefined,
                        onClick: reset,
                        disabled: isFormEmpty,
                    },
                    {
                        text: 'Продолжить',
                        className: position === 'mobile' ? b('mobile-nav-button') : undefined,
                        onClick: handleNextStep,
                        accent: true,
                    },
                ];
            case 2:
                return [
                    {
                        text: 'Опубликовать',
                        onClick: handleNextStep,
                        accent: true,
                    },
                ];
            default:
                return [];
        }
    };

    const navigationElements: NavigationElements = {
        desktop: getNavigationElements('desktop'),
        mobile: getNavigationElements('mobile'),
    };

    const renderHeaderText = () => {
        return <p className={b('header-text')}>{step < 2 ? 'Опубликовать промпт' : 'Предпросмотр'}</p>;
    };

    const renderEditButton = () => {
        return (
            <button className={b('header-button')} onClick={handlePrevStep}>
                <Icon name="edit" size={24} />
            </button>
        );
    };

    const headerElements: HeaderElements = {
        desktop: {
            left: {
                type: 'logo',
            },
            center: {
                type: 'custom',
                render: renderHeaderText,
            },
            right: {
                type: 'profile',
            },
        },
        mobile: {
            left:
                step !== 2
                    ? {
                        type: 'backButton',
                        onClick: handlePrevStep,
                    }
                    : undefined,
            center: {
                type: 'custom',
                render: renderHeaderText,
            },
            right:
                step === 2
                    ? {
                        type: 'custom',
                        render: renderEditButton,
                    }
                    : undefined,
        },
    };

    return (
        <WebAppLayout navigationElements={navigationElements} headerElements={headerElements}>
            <Stack className={b()}>
                <Steps steps={getSteps()} />
                {step === 0 && (
                    <PromptTextStep
                        promptText={promptText}
                        onPromptTextChange={handlePromptTextChange}
                        error={validationErrors.prompt}
                    />
                )}
                {step === 1 && (
                    <PromptDetailsStep
                        promptText={promptText}
                        onPromptTextChange={handlePromptTextChange}
                        promptTitle={promptTitle}
                        onPromptTitleChange={handlePromptTitleChange}
                        promptProblem={promptProblem}
                        onPromptProblemChange={handlePromptProblemChange}
                        selectedCategories={selectedCategories}
                        onChangeCategories={handleChangeCategories}
                        validationErrors={validationErrors}
                    />
                )}
                {step === 2 && (
                    <PromptPreviewStep
                        promptText={promptText}
                        promptTitle={promptTitle}
                        promptProblem={promptProblem}
                        selectedCategories={selectedCategories}
                        handlePrevStep={handlePrevStep}
                    />
                )}
            </Stack>
        </WebAppLayout>
    );
};
